"use client";

import { showAlertModal, showConfirmModal, showLoadingModal } from "@/models";
import { useAdminContext } from "@/providers/AdminProvider";
import { IAchievementForm } from "@/types";
import { getErrorMessage } from "@/utils/error";
import { useRouter } from "next/navigation";

export const useAchievementOperations = () => {
    const { setAchievements } = useAdminContext();
    const router = useRouter();

    const createAchievement = async (achievement: IAchievementForm) => {
        const closeLoadingModal = showLoadingModal({
            message: "Adding achievement...",
        });
        try {
            const res = await fetch("/api/achievements", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(achievement),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            setAchievements((prev) => [...prev, data.data]);
            closeLoadingModal();
            showAlertModal({ message: "Achievement added", type: "success" });
            router.push("/admin/achievements/");
            return data.data;
        } catch (error) {
            closeLoadingModal();
            showAlertModal({ message: getErrorMessage(error), type: "error" });
        }
    };

    const updateAchievement = async (achievement: IAchievementForm) => {
        const closeLoadingModal = showLoadingModal({
            message: "Updating achievement...",
        });
        try {
            const res = await fetch(`/api/achievements/${achievement._id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(achievement),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            setAchievements((prev) =>
                prev.map((proj) =>
                    proj._id === data.data._id ? data.data : proj,
                ),
            );

            closeLoadingModal();
            showAlertModal({ message: "Achievement updated", type: "success" });
            router.push("/admin/achievements/");
            return data.data;
        } catch (error) {
            closeLoadingModal();
            showAlertModal({ message: getErrorMessage(error), type: "error" });
        }
    };

    const deleteAchievement = async (id: string) => {
        const confirmed = await showConfirmModal({
            title: "Delete Achievement",
            message: "Are you sure you want to delete this achievement?",
            type: "delete",
        });

        if (!confirmed) return;

        const closeLoadingModal = showLoadingModal({
            message: "Deleting achievement...",
        });
        try {
            const res = await fetch(`/api/achievements/${id}`, {
                method: "DELETE",
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            setAchievements((prev) => prev.filter((proj) => proj._id !== id));

            closeLoadingModal();
            showAlertModal({ message: "Achievement deleted", type: "success" });
            router.push("/admin/achievements/");
            return id;
        } catch (error) {
            closeLoadingModal();
            showAlertModal({ message: getErrorMessage(error), type: "error" });
        }
    };

    return {
        createAchievement,
        updateAchievement,
        deleteAchievement,
    };
};
